/**
 * Browser-side SSE client for /api/v1/ai/chat.
 *
 * EventSource only does GET, so we use fetch + ReadableStream and parse the
 * `event:` / `data:` lines ourselves. The async generator yields one parsed
 * {@link StreamEvent} per frame; callers should `for await` over it and
 * dispatch into their state reducer.
 */

import { useAuthStore } from '../../lib/authStore.js';

import type { StreamEvent } from './types.js';

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) || '/api/v1';

export interface ChatRequest {
  message: string;
  conversationId?: string;
  provider?: string;
  model?: string;
  pageContext?: { route?: string; entityType?: string; entityId?: string };
}

export async function* streamChat(
  body: ChatRequest,
  signal?: AbortSignal,
): AsyncGenerator<StreamEvent> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${BASE}/ai/chat`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
    credentials: 'include',
  });
  if (!res.ok) {
    let detail = '';
    try { detail = await res.text(); } catch { /* ignore */ }
    throw new Error(`ai_http_${res.status}${detail ? `: ${detail.slice(0, 200)}` : ''}`);
  }
  if (!res.body) throw new Error('ai_no_stream_body');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buffer.indexOf('\n\n')) !== -1) {
        const chunk = buffer.slice(0, nl);
        buffer = buffer.slice(nl + 2);
        const dataLine = chunk.split('\n').find((l) => l.startsWith('data: '));
        if (!dataLine) continue;
        const data = dataLine.slice('data: '.length).trim();
        if (!data) continue;
        try {
          yield JSON.parse(data) as StreamEvent;
        } catch {
          // skip malformed
        }
      }
    }
  } finally {
    try { reader.releaseLock(); } catch { /* noop */ }
  }
}
